import { getI18n } from "@/plugins/0.i18n"
import { showToast } from "./toast"
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js'
import DOMPurify from 'dompurify'

const i18n = getI18n().global

//Get initials from given string
//"Dariusz Kamiński" => "DK"
const getInitials = (name) => {
  if (typeof name !== 'string') return ''

  return name
    .split(' ')
    .filter(part => part)
    .map(part => part[0].toUpperCase())
    .join('')
}

const sanitizeHtml = (html) => {
  return DOMPurify.sanitize(String(html ?? ''), {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'ul',
      'ol',
      'li',
      'a',
      'div',
      'span',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  })
}

/**
 * Normalizes text by removing accents and special characters
 * @param {string} text - The text to normalize
 * @returns {string} Normalized text without diacritical marks
 * @example
 * normalize("Śróba Źródła") => "sroba zrodla"
 */
const normalize = (text) => {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Normalize the string into its decomposed form
    .replace(/ł/g, 'l') // Ł or ł is not included in \u0300-\u036f
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks (accents)
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove invalid characters (keep letters, numbers, spaces, and hyphens)
}

//"Śróba Źródła" => "sroba-zrodla"
const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Normalize the string into its decomposed form
    .replace(/ł/g, 'l') // Ł or ł is not included in \u0300-\u036f
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks (accents)
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove invalid characters (keep letters, numbers, spaces, and hyphens)
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') //Replace multiple hyphens with a single one
}
/**
 * Generates a unique string ID
 * @param {number} [length=16] - The length of the ID (default: 16)
 * @returns {string} A unique string ID
 */
const generateUniqueId = (length = 16) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const timestamp = Date.now().toString(36)
  const randomPart = Array.from(
    { length: length - timestamp.length },
    () => chars[Math.floor(Math.random() * chars.length)],
  ).join('')

  return `${timestamp}${randomPart}`
}


/**
   * Formats a salon name by replacing HTML entity `&#39;` with an apostrophe `'`.
   * This is useful for cleaning up improperly encoded strings.
   *
   * @param {string} name - The salon name to format.
   * @returns {string} The formatted salon name with HTML entities replaced.
   */
const formatSalonerName = (name) => {
  return name
    .replaceAll("&#39;", "'")
    .replaceAll("&amp;#39", "'")
}

/**
 * Formats a number according to Polish price formatting standards
 * @param {number|string} value - The number to format
 * @param {boolean} [addCurrency=false] - Whether to add currency symbol
 * @param {string} [currencySymbol='zł'] - Currency symbol to use
 * @param {number} [decimals=2] - Number of decimal places
 * @returns {string} Formatted number according to Polish standards
 * @example
 * formatNumber(1000) => "1 000"
 * formatNumber(1000.5) => "1 000,50"
 * formatNumber(10000, true) => "10 000 zł"
 * formatNumber("1234.56") => "1 234,56"
 * formatNumber(100) => "100"
 */
const formatNumber = (value, addCurrency = false, currencySymbol = 'zł', decimals = 2) => {
  // Handle invalid inputs
  if (value === null || value === undefined) return ''

  // Convert to number and handle formatting
  const num = Number(value)
  if (isNaN(num)) return ''

  // Format with toFixed first to get the exact decimal precision
  const fixedNum = num.toFixed(decimals)

  // Check if the decimal part is all zeros
  const hasSignificantDecimals = fixedNum.includes('.') &&
    !fixedNum.endsWith('.' + '0'.repeat(decimals))

  // Format with appropriate decimal places
  const formatted = num.toLocaleString('pl-PL', {
    minimumFractionDigits: hasSignificantDecimals ? decimals : 0,
    maximumFractionDigits: decimals,
  })

  // Add currency symbol if requested
  return addCurrency ? `${formatted} ${currencySymbol}` : formatted
}


/**
 * Limits a value to a specified maximum length after converting to string
 * @param {*} value - The value to limit
 * @param {number} maxLength - Maximum length of the resulting string
 * @param {string} [defaultValue=''] - Default value if input is falsy
 * @param {string} [finishingStr=''] - String that will be added at the end when limit is exceeded
 * @returns {string} The string limited to maxLength characters
 * @example
 * limitStringLength("Hello World", 5) => "Hello"
 * limitStringLength(12345, 3) => "123"
 * limitStringLength(null, 10) => ""
 * limitStringLength(undefined, 10, "N/A") => "N/A"
 * limitStringLength("Hello World", 3, '', '...') => "Hel..."
 */
const limitStringLength = (value, maxLength, defaultValue = '', finishingStr = '') => {
  if (value === null || value === undefined) return defaultValue

  return value.toString().slice(0, maxLength) + (value.toString().length > maxLength ? finishingStr : '')
}
const limitStringLengthSafeHTML = (value, maxLength, defaultValue = '', finishingStr = '') => {
  if (value === null || value === undefined) return defaultValue

  const str = value.toString()
  let visibleCount = 0
  let result = ''
  let insideTag = false

  for (let i = 0; i < str.length; i++) {
    const char = str[i]

    if (char === '<') insideTag = true
    if (!insideTag) visibleCount++
    result += char
    if (char === '>') insideTag = false

    if (visibleCount >= maxLength && !insideTag) {
      if (i < str.length - 1) result += finishingStr // Add finishingStr only if it was cut
      break
    }
  }

  return result
}

/**
 * Copies text to clipboard
 * @param {string} text - The text to copy to clipboard
 * @returns {Promise<void>} A promise that resolves when the text is copied
 * @example
 * copyToClipboard("Hello World")
 */
const copyToClipboard = async (text, showNotification = false) => {
  try {
    await navigator.clipboard.writeText(text)
    if (showNotification) {
      showToast(i18n.t('clipboard.text_copied_to_clipboard'), 'success')
    }
  } catch (err) {
    console.error('Failed to copy text: ', err)
    if (showNotification) {
      showToast(i18n.t('clipboard.failed_to_copy_text'), 'error')
    }
  }
}


/**
 * Ensures value is a positive number and limits its length
 * @param {*} value - The input value to process
 * @param {number} [maxLength=12] - Maximum length of the resulting string
 * @param preserveLeadingZeros
 * @returns {string} Valid positive number as string or empty string
 * @example
 * ensurePositiveNumber("123", 5) => "123"
 * ensurePositiveNumber("-5", 10) => ""
 * ensurePositiveNumber("0", 10) => ""
 * ensurePositiveNumber("12345", 3) => "123"
 */

const handlePositiveNumberInput = (value, maxLength = 12, preserveLeadingZeros = true) => {
  // If empty, return empty string
  if (value === null || value === undefined || value === '') return ''

  // Convert to string if not already
  let strValue = String(value)

  // Remove any minus signs to ensure only positive numbers
  strValue = strValue.replace(/-/g, '')

  // Special case: just "0" should remain "0"
  if (strValue === '0') return '0'

  // If we already have 0, (user is typing decimals), preserve it
  if (strValue.startsWith('0.')) return strValue

  // Check if input starts with '0' followed by a digit (like "01") and doesn't have a comma/dot already
  if (preserveLeadingZeros && strValue.match(/^0\d/) && !strValue.includes('.')) {
    // Convert to format "0.X" where X is the digit after 0
    return `0.${strValue.substring(1)}`
  }

  // Ensure only one decimal point
  let cleanValue = strValue
  // Replace any commas with periods for number input compatibility
  cleanValue = cleanValue.replace(',', '.')

  const parts = cleanValue.split('.')
  if (parts.length > 2) {
    cleanValue = parts[0] + '.' + parts.slice(1).join('')
  }

  // Truncate to max length
  if (maxLength && cleanValue.length > maxLength) {
    cleanValue = cleanValue.substring(0, maxLength)
  }

  return cleanValue
}


/**
 * Prevents typing specified characters in an input field.
 * Use as a beforeinput event handler (e.g., @beforeinput).
 * @param {InputEvent} e - The beforeinput event.
 * @param {string[]} disabledKeys - Characters to block from input (e.g. ['-']).
 *
 * @example
 * <input @beforeinput="(e) => blockCharacterInput(e, ['-'])" />
 */
const blockCharsInput = (e, disabledKeys) => {
  if (disabledKeys.includes(e.data)) e.preventDefault()
}
/**
 * Prevents typing minus in an input field.
 * @param {InputEvent} e - The beforeinput event.
 *
 * @example
 * <input @beforeinput="blockMinusInput" />
 */
const blockMinusInput = (e) => {
  blockCharsInput(e, ['-', '−']) // B: 1 minus ASCII, 2 minus Unicode - to są różne minusy!!!
}


/**
 * Formats a phone number string by grouping digits into blocks of three.
 * Preserves the leading "+" sign if present and ignores all non-digit characters.
 * Works with numbers from different countries and with or without a country prefix.
 *
 * @param {string|number} input - Raw phone number value to format.
 * @returns {string} Formatted phone number.
 *
 * @example
 * formatPhone('793123149')        // "793 123 149"
 * formatPhone('+48793123149')     // "+48 793 123 149"
 * formatPhone('+1 (555) 123-456') // "+1 555 123 456"
 */
const formatPhone = (input) => {
  //1st try to use libphone
  try {
    if (isValidPhoneNumber(input)) {
      const parsedPhoneNumberObject = parsePhoneNumber(input)
      if(parsedPhoneNumberObject) {
        const parts = []
        for (let i = parsedPhoneNumberObject.nationalNumber.length; i > 0; i -= 3) {
          parts.unshift(parsedPhoneNumberObject.nationalNumber.slice(Math.max(0, i - 3), i))
        }

        return '+'+parsedPhoneNumberObject.countryCallingCode+' '+parts.join(' ')
      }
    }
  } catch (error) {
    //
  }

  //if not possible go with standard 3digit division

  const raw = String(input ?? '').trim()
  if (!raw) return ''

  const hasPlus = raw.startsWith('+')
  //const digits = raw.replace(/\D/g, '') // only digits
  const digits = raw.replace(/[^\d*]/g, '') // only digits and *


  if (!digits) return hasPlus ? '+' : ''

  // group by 3 from the end: 793123149 -> 793 123 149, 48793123149 -> 48 793 123 149
  const parts = []
  for (let i = digits.length; i > 0; i -= 3) {
    parts.unshift(digits.slice(Math.max(0, i - 3), i))
  }

  return (hasPlus ? '+' : '') + parts.join(' ')
}

/**
 * Get Polish plural form for "produkt" based on count
 * Polish has 3 forms: singular (1), few (2-4, 22-24...), many (0, 5-21, 25-31...)
 *
 * @param {number} count - Number to pluralize
 * @returns {string} Translation key suffix ('one', 'few', 'many')
 */
const getPolishPluralForm = (count) => {
  if (count === 1) {
    return 'one'
  }

  const lastDigit = count % 10
  const lastTwoDigits = count % 100

  // 2-4, but not 12-14
  if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 10 || lastTwoDigits >= 20)) {
    return 'few'
  }

  return 'many'
}

/**
 * Truncates text based on character limit and/or newline limit.
 *
 * @param {string} text - The text to truncate
 * @param {number|null} [limit=80] - Maximum number of characters (set to null to ignore)
 * @param {number} [maxLines=3] - Maximum number of lines
 * @param {string} [suffix='... '] - Suffix to append if truncated
 */
const truncateTextWithLines = (text, limit = null, maxLines = 3, suffix = '... ') => {
  if (!text) return { text: '', isTruncated: false }

  const lines = text.split('\n')
  const hasTooManyLines = lines.length > maxLines
  const isTooLong = limit ? text.length > limit : false

  // Tekst jest ucinany jeśli ma za dużo "twardych" linii LUB za dużo znaków
  const isTruncated = isTooLong || hasTooManyLines

  if (!isTruncated) {
    return { text, isTruncated: false }
  }

  let resultText = text

  // Jeśli są twarde entery, najpierw tniemy po nich
  if (hasTooManyLines) {
    resultText = lines.slice(0, maxLines).join('\n')
  }

  // Jeśli po cięciu po liniach (lub braku enterów) tekst wciąż jest za długi znakowo - tniemy po znakach
  if (limit && resultText.length > limit) {
    resultText = resultText.slice(0, limit)
  }

  return {
    text: resultText.trimEnd() + suffix,
    isTruncated: true,
  }
}


/**
 * Check if a string is a valid international phone number
 * Supports optional country codes (e.g. +48, +49, +1) and spaces
 * Accepts numbers containing 7 to 15 digits
 *
 * @param {string} value - Phone number to validate
 * @returns {boolean} True if the phone number format is valid, false otherwise
 */
const isPhoneNumber = (value) => {
  if (typeof value !== 'string') return false

  const normalized = value.replace(/\s/g, '')

  return /^\+?[0-9]{7,15}$/.test(normalized)
}

export {
  sanitizeHtml,
  getInitials,
  slugify,
  generateUniqueId,
  formatSalonerName,
  formatNumber,
  formatPhone,
  normalize,
  limitStringLength,
  limitStringLengthSafeHTML,
  copyToClipboard,
  handlePositiveNumberInput,
  blockCharsInput,
  blockMinusInput,
  getPolishPluralForm,
  truncateTextWithLines,
  isPhoneNumber
}
